import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  Bot,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  BriefcaseBusiness,
  CloudOff,
  LayoutDashboard,
  LogOut,
  MoreHorizontal,
  Package,
  Plus,
  RefreshCw,
  Settings,
  SlidersHorizontal,
  Users,
  UserCheck,
  Wifi,
  Wrench,
  X,
} from "lucide-react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./auth";
import { NotificationCenter } from "./NotificationCenter";
import { getQueueStatus, replayQueue } from "./offline-queue";
import { useMediaQuery } from "./hooks";
import logoUrl from "../../../public/logo-ja.webp";

const buildEnvironment = import.meta.env.VITE_APP_ENV === "production" ? "Produção" : "Local";
const fullBuildVersion = import.meta.env.VITE_BUILD_VERSION;

const agentInvitePhrases = [
  "Quer consultar algo no sistema?",
  "Posso ajudar com a agenda.",
  "Precisa montar um orçamento?",
  "Quer conferir clientes ou valores?",
] as const;

export function formatBuildLabel(environment: "Produção" | "Local", version: string) {
  if (environment === "Local") return "dev";
  const compactVersion = version.replace(/\D/g, "");
  return `b${compactVersion || version}`;
}

export const buildLabel = formatBuildLabel(buildEnvironment, fullBuildVersion);

const primaryNav: Array<{
  to: string;
  label: string;
  mobileLabel?: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  permission?: string;
}> = [
  { to: "/", label: "Visão geral", icon: LayoutDashboard, end: true },
  { to: "/orcamentos", label: "Orçamentos", icon: BriefcaseBusiness, permission: "quotes.read" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, permission: "schedule.read" },
  { to: "/ordens", label: "Ordens de serviço", mobileLabel: "Ordens", icon: Wrench, permission: "work_orders.read" },
  { to: "/clientes", label: "Clientes", icon: Users, permission: "customers.read" },
  { to: "/financeiro", label: "Financeiro", icon: CircleDollarSign, permission: "finance.read" },
];

const baseSecondaryNav = [
  { to: "/equipe", label: "Equipe", icon: UserCheck, end: false, permission: "people.read" },
  { to: "/servicos", label: "Serviços", icon: BriefcaseBusiness, end: false, permission: "catalog.read" },
  { to: "/estoque", label: "Estoque", icon: Package, end: false, permission: "inventory.read" },
  { to: "/configuracoes", label: "Configurações", icon: Settings, end: false },
];

export const visibleNavigationPaths = (permissions: string[]) => {
  const allowed = new Set(permissions);
  const visible = <T extends { permission?: string }>(item: T) => !item.permission || allowed.has(item.permission);
  return {
    primary: primaryNav.filter(visible).map(({ to }) => to),
    secondary: baseSecondaryNav.filter(visible).map(({ to }) => to),
    floating: ["/assistente"],
  };
};

export function AgentShortcut({ obscured = false }: { obscured?: boolean }) {
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [inviteIndex, setInviteIndex] = useState<number | null>(null);
  const nextInviteRef = useRef(0);
  useEffect(() => {
    const update = (event: Event) => setExpanded(Boolean((event as CustomEvent<boolean>).detail));
    window.addEventListener("ja:agent-open-change", update);
    return () => window.removeEventListener("ja:agent-open-change", update);
  }, []);
  useEffect(() => {
    if (expanded || obscured || location.pathname.startsWith("/assistente")) {
      setInviteIndex(null);
      return;
    }
    let hideTimer: number | undefined;
    const showInvite = () => {
      if (document.visibilityState === "hidden") return;
      setInviteIndex(nextInviteRef.current);
      nextInviteRef.current = (nextInviteRef.current + 1) % agentInvitePhrases.length;
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => setInviteIndex(null), 6_000);
    };
    const firstInvite = window.setTimeout(showInvite, 10_000);
    const recurringInvite = window.setInterval(showInvite, 70_000);
    return () => {
      window.clearTimeout(firstInvite);
      window.clearTimeout(hideTimer);
      window.clearInterval(recurringInvite);
    };
  }, [expanded, location.pathname, obscured]);
  if (location.pathname.startsWith("/assistente")) return null;
  const toggleAgent = () => {
    setInviteIndex(null);
    window.dispatchEvent(new CustomEvent("ja:agent-toggle"));
  };
  return <>
    {inviteIndex !== null && !expanded && !obscured && <button
      type="button"
      className="agent-floating-invite"
      aria-label={`${agentInvitePhrases[inviteIndex]} Abrir Assistente JA`}
      onClick={toggleAgent}
    >
      {agentInvitePhrases[inviteIndex]}
    </button>}
    <button
      type="button"
      className={`agent-floating-shortcut${obscured ? " is-obscured" : ""}${expanded ? " is-expanded" : ""}`}
      aria-label="Abrir Assistente JA"
      aria-haspopup="dialog"
      aria-expanded={expanded}
      title="Assistente JA"
      tabIndex={obscured ? -1 : undefined}
      onClick={toggleAgent}
    >
      <Bot size={26} strokeWidth={2.1} />
    </button>
  </>;
}

const statusLabels: Record<string, string> = {
  planned: "Planejada", preparation: "Em preparação", traveling: "Em deslocamento", on_site: "No local",
  in_progress: "Em execução", paused: "Pausada", pending_completion: "Aguardando conclusão",
  incomplete: "Incompleta", completed: "Concluída", closed: "Encerrada", cancelled: "Cancelada",
  not_performed: "Não realizada", pending: "Pendente", in_review: "Em revisão", issued: "Emitido",
  not_billed: "Não faturada", partially_billed: "Faturamento parcial", billed: "Aguardando pagamento",
  partially_paid: "Pagamento parcial", paid: "Paga", overdue: "Vencida", refunded: "Estornada",
  awaiting_confirmation: "Aguardando confirmação", confirmed: "Confirmado", active: "Ativo",
  rescheduled: "Reagendado",
  settled: "Liquidado", approved: "Aprovada", reported: "Informado", recognized: "Reconhecida",
  pending_approval: "Aguardando aprovação", rejected: "Rejeitada", reversed: "Estornado", failed: "Falhou",
  draft: "Rascunho", ready_to_send: "Pronto para envio", sent: "Enviado", accepted: "Aceito", refused: "Recusado", expired: "Vencido",
  invited: "Convidado", locked: "Bloqueado", revoked: "Revogado",
  inactive: "Inativa", calculated: "Calculado", superseded: "Substituído",
  awaiting_payment: "Aguardando pagamento",
  pending_data: "Dados pendentes", ready: "Pronta", queued: "Na fila", processing: "Emitindo",
  authorized: "Autorizada", unknown: "Verificação pendente", cancel_pending: "Cancelamento pendente",
  replaced: "Substituída",
};

export function AppShell() {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [online, setOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState({ total: 0, failed: 0 });
  const [desktopMoreOpen, setDesktopMoreOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const desktopMoreRef = useRef<HTMLDivElement>(null);
  const permissionSet = new Set(user?.permissions ?? []);
  const visible = <T extends { permission?: string }>(item: T) => !item.permission || permissionSet.has(item.permission);
  const visiblePrimaryNav = primaryNav.filter(visible);
  const secondaryNav = baseSecondaryNav.filter(visible);
  const desktopSecondaryNav = secondaryNav;
  const mobilePrimaryNav = visiblePrimaryNav.slice(0, 4);
  const mobileOverflowNav = visiblePrimaryNav.slice(4);

  useEffect(() => {
    const update = () => {
      setOnline(navigator.onLine);
      void getQueueStatus().then(setQueue);
    };
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    window.addEventListener("ja:queue-changed", update);
    update();
    if (navigator.onLine) void replayQueue();
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
      window.removeEventListener("ja:queue-changed", update);
    };
  }, []);

  useEffect(() => {
    if (!desktopMoreOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!desktopMoreRef.current?.contains(event.target as Node)) setDesktopMoreOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDesktopMoreOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [desktopMoreOpen]);

  useEffect(() => {
    setDesktopMoreOpen(false);
    setMobileMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMoreOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMoreOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileMoreOpen]);

  return <div className={`app-shell${location.pathname.startsWith("/assistente") ? " is-agent-route" : ""}`}>
    <div className="app-utility">
      <div className="app-container utility-content">
        <span className={online ? "online-state" : "offline-state"}>{online ? <Wifi size={14} /> : <CloudOff size={14} />}{online ? "Sistema online" : "Trabalho offline"}</span>
        <div>
          <span
            className={`build-state${buildEnvironment === "Produção" ? " is-production" : " is-local"}`}
            title={`${buildEnvironment} · build ${fullBuildVersion}`}
          >
            {buildLabel}
          </span>
          {queue.total > 0 && <button className={`sync-button${queue.failed ? " sync-button-error" : ""}`} onClick={() => void replayQueue()}><RefreshCw size={13} /> {queue.failed ? `${queue.failed} falha${queue.failed > 1 ? "s" : ""} de sincronização` : `${queue.total} ação${queue.total > 1 ? "ões" : ""} para sincronizar`}</button>}
        </div>
      </div>
    </div>

    <header className="app-header">
      <div className="app-container header-content">
        <NavLink to="/" className="app-brand" aria-label="JA Soluções Prediais — início da plataforma">
          <img src={logoUrl} alt="JA Soluções Prediais" />
        </NavLink>
        <nav className="desktop-app-nav" aria-label="Navegação da plataforma">
          {visiblePrimaryNav.map(({ to, label, end }) => <NavLink key={to} to={to} end={end}>{label}</NavLink>)}
          <div className="desktop-more" ref={desktopMoreRef}>
            <button className={desktopSecondaryNav.some(({ to }) => location.pathname.startsWith(to)) ? "desktop-more-trigger active" : "desktop-more-trigger"} onClick={() => setDesktopMoreOpen((value) => !value)} aria-expanded={desktopMoreOpen} aria-controls="desktop-more-menu">Mais</button>
            {desktopMoreOpen && <div className="desktop-more-menu" id="desktop-more-menu">{desktopSecondaryNav.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} onClick={() => setDesktopMoreOpen(false)}><Icon size={18} />{label}</NavLink>)}</div>}
          </div>
        </nav>
        <div className="header-actions">
          {permissionSet.has("quotes.write") && <NavLink className="site-button site-button-red site-button-small global-create" to="/orcamentos/novo" aria-label="Novo orçamento"><Plus size={16} /><span>Novo orçamento</span></NavLink>}
          <NavLink className="logout-button admin-shortcut" to="/configuracoes" aria-label="Configurações"><Settings size={19} /></NavLink>
          <NotificationCenter />
          <button className="logout-button" onClick={() => void logout()} aria-label="Sair da conta"><LogOut size={19} /></button>
        </div>
      </div>
    </header>

    <main className="app-main"><Outlet /></main>

    <AgentShortcut obscured={mobileMoreOpen} />

    <button className={`mobile-more-backdrop${mobileMoreOpen ? " is-open" : ""}`} aria-label="Fechar mais opções" aria-hidden={!mobileMoreOpen} tabIndex={mobileMoreOpen ? 0 : -1} onClick={() => setMobileMoreOpen(false)} />
    <div className={`mobile-nav-shell${mobileMoreOpen ? " is-open" : ""}`}>
      <aside className="mobile-more-tray" id="mobile-more-menu" aria-label="Mais opções" aria-hidden={!mobileMoreOpen} inert={mobileMoreOpen ? undefined : true}>
        <div className="mobile-more-grid">
          {mobileOverflowNav.map(({ to, label, icon: Icon }) => <NavLink className="mobile-more-option" key={to} to={to} onClick={() => setMobileMoreOpen(false)}><Icon size={21} /><small>{label}</small></NavLink>)}
          {secondaryNav.map(({ to, label, icon: Icon }) => <NavLink className="mobile-more-option" key={to} to={to} onClick={() => setMobileMoreOpen(false)}><Icon size={21} /><small>{label}</small></NavLink>)}
          <button className="mobile-more-option mobile-more-logout" onClick={() => void logout()}><LogOut size={21} /><small>Sair</small></button>
        </div>
      </aside>
      <nav className="mobile-app-nav" aria-label="Navegação móvel" style={{ gridTemplateColumns: `repeat(${mobilePrimaryNav.length + 1}, minmax(0, 1fr))` }}>
        {mobilePrimaryNav.map(({ to, label, mobileLabel, icon: Icon, end }) => <NavLink key={to} to={to} end={end} onClick={() => setMobileMoreOpen(false)}><Icon size={20} /><small>{mobileLabel ?? label}</small></NavLink>)}
        <button className={mobileMoreOpen || secondaryNav.some(({ to }) => location.pathname.startsWith(to)) || mobileOverflowNav.some(({ to }) => location.pathname.startsWith(to)) ? "active" : ""} onClick={() => setMobileMoreOpen((value) => !value)} aria-expanded={mobileMoreOpen} aria-controls="mobile-more-menu" aria-label={mobileMoreOpen ? "Fechar mais opções" : "Abrir mais opções"}>{mobileMoreOpen ? <X size={20} /> : <MoreHorizontal size={20} />}<small>Mais</small></button>
      </nav>
    </div>
  </div>;
}

export function ResponsiveFilters({ children, activeCount = 0, summary, label = "Filtros", className = "" }: {
  children: ReactNode;
  activeCount?: number;
  summary?: string;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  return <div className={`responsive-filters${open ? " is-open" : ""}${className ? ` ${className}` : ""}`}>
    <button className="responsive-filter-trigger" type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls={contentId}>
      <span className="responsive-filter-icon"><SlidersHorizontal size={18} /></span>
      <span className="responsive-filter-copy"><strong>{label}</strong>{summary && <small>{summary}</small>}</span>
      {activeCount > 0 && <span className="responsive-filter-count" aria-label={`${activeCount} ${activeCount === 1 ? "filtro ativo" : "filtros ativos"}`}>{activeCount}</span>}
      <ChevronDown className="responsive-filter-chevron" size={18} />
    </button>
    <div className="responsive-filter-content" id={contentId}>{children}</div>
  </div>;
}

export function ResponsiveDetailSurface({ children, title, onClose, className = "" }: {
  children: ReactNode;
  title: string;
  onClose: () => void;
  className?: string;
}) {
  const mobile = useMediaQuery("(max-width: 780px)");
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const historyMarkerRef = useRef(`ja-detail-${Math.random().toString(36).slice(2)}`);
  const closedFromHistoryRef = useRef(false);
  onCloseRef.current = onClose;

  const closeSurface = () => {
    closedFromHistoryRef.current = true;
    if (mobile && window.history.state?.jaDetailSurface === historyMarkerRef.current) window.history.back();
    onCloseRef.current();
  };

  useEffect(() => {
    if (!mobile) return;
    const marker = historyMarkerRef.current;
    const openedUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const currentState = window.history.state;
    closedFromHistoryRef.current = false;
    if (currentState?.jaDetailSurface !== marker) {
      window.history.pushState({
        ...(currentState && typeof currentState === "object" ? currentState : {}),
        jaDetailSurface: marker,
      }, "", window.location.href);
    }
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    let historyEntryActive = false;
    const activationTimer = window.setTimeout(() => { historyEntryActive = true; }, 0);
    const closeOnHistoryBack = (event: PopStateEvent) => {
      if (event.state?.jaDetailSurface === marker) return;
      closedFromHistoryRef.current = true;
      onCloseRef.current();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeSurface();
    };
    window.addEventListener("popstate", closeOnHistoryBack);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.clearTimeout(activationTimer);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("popstate", closeOnHistoryBack);
      document.removeEventListener("keydown", closeOnEscape);
      const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (historyEntryActive && !closedFromHistoryRef.current && currentUrl === openedUrl && window.history.state?.jaDetailSurface === marker) {
        window.history.back();
      }
      previousFocus?.focus();
    };
  }, [mobile]);

  const surface = <section className={`responsive-detail-surface${mobile ? " is-mobile" : ""}${className ? ` ${className}` : ""}`} aria-label={title} role={mobile ? "dialog" : undefined} aria-modal={mobile ? "true" : undefined}>
    <header className="responsive-detail-mobile-header">
      <button ref={closeButtonRef} type="button" onClick={closeSurface}><ArrowLeft size={18} /> Voltar</button>
      <strong>{title}</strong>
    </header>
    <div className="responsive-detail-content">{children}</div>
  </section>;

  return mobile ? createPortal(surface, document.body) : surface;
}

export function PageBackLink({ to, context }: { to: string; context: string }) {
  return <div className="page-back-row">
    <Link className="hero-back" to={to}><ArrowLeft size={18} /> Voltar</Link>
    <strong>{context}</strong>
  </div>;
}

export function PageHeader({ eyebrow, title, description, action, dark = false }: {
  eyebrow?: string; title: ReactNode; description?: string; action?: ReactNode; dark?: boolean;
}) {
  return <header className={`section-heading app-page-heading${dark ? " dark" : ""}`}>
    <div>{eyebrow && <span className="site-eyebrow">{eyebrow}</span>}<h1>{title}</h1>{description && <p>{description}</p>}</div>
    {action && <div className="page-actions">{action}</div>}
  </header>;
}

export function StateView({ loading, error, empty, emptyTitle = "Nenhum registro", emptyMessage, children }: {
  loading: boolean; error?: string; empty?: boolean; emptyTitle?: string; emptyMessage?: string; children: ReactNode;
}) {
  if (loading) return <div className="state-card" aria-live="polite"><RefreshCw className="spin" size={24} /><strong>Carregando…</strong></div>;
  if (error) return <div className="state-card state-error" role="alert"><CloudOff size={25} /><strong>Não foi possível carregar</strong><span>{error}</span></div>;
  if (empty) return <div className="state-card"><CheckCircle2 size={25} /><strong>{emptyTitle}</strong>{emptyMessage && <span>{emptyMessage}</span>}</div>;
  return <>{children}</>;
}

export function StatusBadge({ value }: { value: string }) {
  return <span className={`status status-${value}`}>{statusLabels[value] ?? value.replaceAll("_", " ")}</span>;
}

export function InfoCard({ icon: Icon, label, value, detail, tone = "navy" }: {
  icon: typeof CalendarDays; label: string; value: ReactNode; detail?: string;
  tone?: "navy" | "red" | "soft" | "blue" | "indigo" | "violet" | "green" | "amber";
}) {
  return <article className={`info-card tone-${tone}`}><span className="info-icon"><Icon size={21} /></span><div><span>{label}</span><strong>{value}</strong>{detail && <small>{detail}</small>}</div></article>;
}
